<template>
    <header
        v-if="banners.length"
        class="relative flex h-12 px-4 shrink-0 items-center gap-3 text-sm font-medium"
        :class="current.bgClass"
    >
        <!-- Current banner content -->
        <div class="flex-1 flex items-center min-w-0">
            <component :is="current.component" v-bind="current.props" />
        </div>

        <!-- Prev / Next grouped at far right -->
        <div
            v-if="banners.length > 1"
            class="flex items-center gap-1 shrink-0 ml-2"
        >
            <button
                type="button"
                class="cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Previous banner"
                @click="prev"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="h-4 w-4"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15.75 19.5L8.25 12l7.5-7.5"
                    />
                </svg>
            </button>
            <button
                type="button"
                class="cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Next banner"
                @click="next"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="h-4 w-4"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8.25 4.5l7.5 7.5-7.5 7.5"
                    />
                </svg>
            </button>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
    banners: { type: Array, default: () => [] },
})

const currentIndex = ref(0)

const current = computed(
    () => props.banners[currentIndex.value] ?? props.banners[0]
)

/** @type {ReturnType<typeof window.setInterval> | null} */
let timer = null
let userNavigated = false

function stopTimer() {
    window.clearInterval(timer)
    timer = null
}

function startTimer() {
    stopTimer()
    if (props.banners.length > 1 && !userNavigated) {
        timer = window.setInterval(() => {
            currentIndex.value = (currentIndex.value + 1) % props.banners.length
        }, 5000)
    }
}

watch(
    () => props.banners,
    (banners) => {
        if (currentIndex.value >= banners.length)
            currentIndex.value = Math.max(0, banners.length - 1)
        userNavigated = false
        startTimer()
    }
)

function prev() {
    userNavigated = true
    stopTimer()
    currentIndex.value =
        (currentIndex.value - 1 + props.banners.length) % props.banners.length
}

function next() {
    userNavigated = true
    stopTimer()
    currentIndex.value = (currentIndex.value + 1) % props.banners.length
}

onMounted(startTimer)
onUnmounted(stopTimer)
</script>
